import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  ButtonGroup,
} from "@mui/material";
import axios from "../../utils/axiosInstance";
import HistoryChartManager from "../HistoryChartManager.jsx";
import { useLocation } from "react-router-dom";
import { useTheme, useMediaQuery } from "@mui/material";
import { toast } from "react-toastify";

// Define register groups for each button
const REGISTER_GROUPS = {
  // MainField: [40506, 41579, 41001, 41002, 41714, 41715],
  // MainFlow: [40506, 41579, 40505, 41517, 41652, 41653, 41654, 41717, 41716],
  MainField: ["FLOW_METER", 'FLOW_RATE_SP'],
  MainFlow: ["FLOW_METER", 'FLOW_RATE_SP', 'DISCHARGE_PRES'],
  PLG1: [
    40510, 40511, 43808, 40509, 44035, 44023, 44019, 44015, 42157, 41719, 41718, 42290, 42291,
    42292, 42152, 42296, 42297, 42298, 42150, 42868, 42872,
  ],
  PLG2: [
    40516, 40517, 43856, 40515, 44036, 44024, 44020, 44016, 41721, 41720, 42168, 42317, 42318,
    42319, 42163, 42323, 42324, 42325, 42161, 42869, 42873,
  ],
  PLG3: [
    40519, 40520, 43904, 40518, 44037, 44025, 44021, 44017, 41723, 41722, 42179, 42344, 42345,
    42346, 42174, 42353, 42354, 42355, 42172, 42870, 42874,
  ],
  PLG4: [
    40522, 40523, 43952, 40521, 44038, 44026, 44022, 44018, 41725, 41724, 42190, 42371, 42372,
    42373, 42185, 42377, 42378, 42379, 42183, 42871, 42875,
  ],
};

// ── Inline time range bar ─────────────────────────────────────────────────────
function TimeRangeBar({ timeRange, setTimeRange, onRefresh, loading }) {
  const [mode, setMode] = useState("preset");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
 
  const handleApplyCustom = () => {
    if (!startDate || !endDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    const start = Math.floor(new Date(startDate).getTime() / 1000);
    const end   = Math.floor(new Date(endDate).getTime() / 1000);
    if (start >= end) {
      toast.error("Start date must be before end date");
      return;
    }
    setTimeRange({ type: "custom", start, end });
  };
 
  const inputCls = "px-2 py-1 rounded bg-slate-800 text-white border border-slate-600 text-sm";
  const labelCls = "text-gray-300 text-xs mb-1";
 
  return (
    <div className="mb-3 text-sm">
      {/* ── Mobile ── */}
      <div className="flex flex-col gap-2 sm:hidden">
        <div className="flex gap-2">
          <div className="flex flex-col flex-1">
            <label className={labelCls}>Range Type</label>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              className={`${inputCls} w-full`}
            >
              <option value="preset">Preset Range</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
 
          {mode === "preset" && (
            <div className="flex flex-col flex-1">
              <label className={labelCls}>Time Range</label>
              <select
                value={timeRange?.hours || 168}
                onChange={(e) =>
                  setTimeRange({ type: "preset", hours: Number(e.target.value) })
                }
                className={`${inputCls} w-full`}
              >
                <option value={4}>Last 4 hours</option>
                <option value={8}>Last 8 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last week</option>
                <option value={336}>Last 2 weeks</option>
              </select>
            </div>
          )}
 
          {mode === "custom" && (
            <div className="flex flex-col flex-1">
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={`${inputCls} w-full`}
              />
            </div>
          )}
        </div>
 
        {mode === "custom" && (
          <div className="flex gap-2">
            <div className="flex flex-col flex-1">
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={`${inputCls} w-full`}
              />
            </div>
          </div>
        )}
 
        <div className="flex gap-2">
          {mode === "custom" && (
            <button
              onClick={handleApplyCustom}
              className="flex-1 px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Apply
            </button>
          )}
          <button
            onClick={onRefresh}
            disabled={loading}
            className={`flex-1 px-3 py-2 rounded text-white font-medium ${
              loading ? "bg-slate-600 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
            }`}
          >
            {loading ? "Loading..." : "↻ Refresh"}
          </button>
        </div>
      </div>
 
      {/* ── Desktop ── */}
      <div className="hidden sm:flex sm:flex-wrap sm:gap-3 sm:items-end">
        <div className="flex flex-col">
          <label className={labelCls}>Range Type</label>
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            className={inputCls}
          >
            <option value="preset">Preset Range</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>
 
        {mode === "preset" && (
          <>
            <div className="flex flex-col">
              <label className={labelCls}>Time Range</label>
              <select
                value={timeRange?.hours || 168}
                onChange={(e) =>
                  setTimeRange({ type: "preset", hours: Number(e.target.value) })
                }
                className={inputCls}
              >
                <option value={4}>Last 4 hours</option>
                <option value={8}>Last 8 hours</option>
                <option value={24}>Last 24 hours</option>
                <option value={168}>Last week</option>
                <option value={336}>Last 2 weeks</option>
              </select>
            </div>
 
            <button
              onClick={onRefresh}
              disabled={loading}
              className={`px-3 py-2 rounded text-white font-medium ${
                loading ? "bg-slate-600 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {loading ? "Loading..." : "↻ Refresh"}
            </button>
          </>
        )}
 
        {mode === "custom" && (
          <>
            <div className="flex flex-col">
              <label className={labelCls}>Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className={inputCls}
              />
            </div>
 
            <div className="flex flex-col">
              <label className={labelCls}>End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className={inputCls}
              />
            </div>
 
            <button
              onClick={handleApplyCustom}
              className="px-3 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Apply
            </button>
 
            <button
              onClick={onRefresh}
              disabled={loading}
              className={`px-3 py-2 rounded text-white font-medium ${
                loading ? "bg-slate-600 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"
              }`}
            >
              {loading ? "Loading..." : "↻ Refresh"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
 
// ── Main component ─────────────────────────────────────────────────────────────

export default function HistoricalSPs({ device }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const location = useLocation();
  const [timeRange, setTimeRange] = useState({
    type: "preset",
    hours: 168,
  });
  const [chartLoading, setChartLoading] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [selectedGroup, setSelectedGroup] = useState("MainField");

  // --- Fetch function with register numbers ---
  const fetchChartData = useCallback(
    async (deviceId, registerGroup) => {
      if (!deviceId) return;
 
      try {
        setChartLoading(true);
 
        // Resolve start / end in unix seconds
        const now = Math.floor(Date.now() / 1000);
        const start = timeRange.type === "custom"
          ? timeRange.start
          : now - timeRange.hours * 3600;
        const end = timeRange.type === "custom"
          ? timeRange.end
          : now;
 
        // Suffix device ID for plunger groups
        let resolvedId = deviceId;
        if (registerGroup === "PLG1") resolvedId = deviceId + "W1";
        if (registerGroup === "PLG2") resolvedId = deviceId + "W2";
        if (registerGroup === "PLG3") resolvedId = deviceId + "W3";
        if (registerGroup === "PLG4") resolvedId = deviceId + "W4";
 
        const registers = REGISTER_GROUPS[registerGroup] || REGISTER_GROUPS.MainField;
 
        // ── Key change: new route (no /25) + ?start=&end= query params ──────
        const url = `${process.env.REACT_APP_API_GATEAWAY}/iot/historical-sp/${resolvedId}?start=${start}&end=${end}`;
 
        const response = await axios.post(url, registers);
 
        const apiData = response.data?.response;
 
        if (!apiData)                            throw new Error("Missing response.data.response");
        if (!apiData.time || !Array.isArray(apiData.time)) throw new Error("Missing 'time' array");
 
        if (apiData.time.length === 0) {
          setChartData(null);
          return;
        }
 
        setChartData(apiData);
      } catch (err) {
        console.error("Failed to fetch historical SP data:", err);
        setChartData(null);
      } finally {
        setChartLoading(false);
      }
    },
    [timeRange]
  );

  // --- Clear data when device changes ---
  useEffect(() => {
    if (device?.id) {
      setChartData(null);
    }
  }, [device?.id]);

  // --- Auto fetch when entering charts tab or changing timeRange or selectedGroup ---
  useEffect(() => {
    fetchChartData(device?.id, selectedGroup);
  }, [device?.id, location.pathname, timeRange, selectedGroup, fetchChartData]);

  // --- Handle button group changes ---
  const handleGroupChange = (group) => {
    setSelectedGroup(group);
    if (device?.id) {
      setChartData(null); // Clear old data immediately for better UX
      // fetchChartData(device?.id, group);
    }
  };

  return (
    <Box
      sx={{
        p: { xs: 1, sm: 2, md: 3 },
        maxWidth: "100%",
      }}
    >
       {/* ── Time range bar ── */}
      <TimeRangeBar
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        onRefresh={() => fetchChartData(device?.id, selectedGroup)}
        loading={chartLoading}
      />

      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: { xs: 1, sm: 1.5 },
          mt: 2,
        }}
      >
        <ButtonGroup
          orientation={isMobile ? "vertical" : "horizontal"}
          variant="contained"
          size={isMobile ? "small" : "medium"}
          sx={{
            width: isMobile ? "100%" : "auto",
            boxShadow: 2,
          }}
        >
          {[
            { key: "MainField", label: "Main Compressor Field Control" },
            { key: "MainFlow", label: "Main Compressor Flow Rate Incremental" },
            { key: "PLG1", label: "Plunger Lift 1" },
            { key: "PLG2", label: "Plunger Lift 2" },
            { key: "PLG3", label: "Plunger Lift 3" },
            { key: "PLG4", label: "Plunger Lift 4" },
          ].map(({ key, label }) => (
            <Button
              key={key}
              fullWidth={isMobile}
              onClick={() => handleGroupChange(key)}
              disabled={chartLoading}
              sx={{
                bgcolor: selectedGroup === key ? "#3b82f6" : "#475569",
                "&:hover": {
                  bgcolor: selectedGroup === key ? "#2563eb" : "#64748b",
                },
                fontWeight: selectedGroup === key ? 600 : 400,
                fontSize: { xs: "12px", sm: "13px", md: "14px" },
                px: { xs: 1.5, sm: 2 },
                transition: "all 0.3s ease",
                color: selectedGroup === key ? "#ffffffff" : "#c0c0c0ff"
              }}
            >
              {label}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Loading State */}
      {chartLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: { xs: "40vh", md: "60vh" },
            gap: 2,
          }}
        >
          <CircularProgress size={isMobile ? 50 : 70} />
          <Typography
            sx={{
              color: "#94a3b8",
              fontSize: { xs: "12px", sm: "14px" },
            }}
          >
            Loading {selectedGroup} data...
          </Typography>
        </Box>
      ) : chartData ? (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <HistoryChartManager
            chartData={chartData}
            selectedGroup={selectedGroup}
          />
        </Box>
      ) : (
        <Box
          sx={{
            p: { xs: 2, sm: 4 },
            bgcolor: "#1e293b",
            borderRadius: 2,
            textAlign: "center",
            border: "1px dashed rgba(255,255,255,0.2)",
            minHeight: { xs: "30vh", md: "40vh" },
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant={isMobile ? "body1" : "h6"}
            sx={{ color: "#94a3b8" }}
          >
            No chart data available
          </Typography>
        </Box>
      )}
    </Box>
  );
}
